/// <amd-module name="Wit/BacklogsHub/Backlog/BacklogViewComponent" />
import { ITeamSettings } from "Agile/Contracts/Common";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import {
    IObservableArray,
    IObservableValue,
    IReadonlyObservableArray,
    ObservableArray,
    ObservableCollection,
    ObservableValue
} from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { ILegacyPlatformService } from "VSS/Legacy/Types";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, IVssLayoutManager, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { IVssTelemetryService, publishEvent } from "VSS/Platform/Telemetry";
import { getFilterItem, getFilterItemIconProps } from "VSSUI/Components/HeaderCommandBar/Items";
import { Toggle } from "VSSUI/Components/Toggle/Toggle";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { FocusZone, FocusZoneContext, FocusZoneDirection } from "VSSUI/FocusZone";
import { Header } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Link } from "VSSUI/Link";
import { IMenuItem, IMenuItemDetails, MenuItemType } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Splitter } from "VSSUI/Splitter";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, Tab, TabBar, TabContent, TabSize } from "VSSUI/Tabs";
import { TeachingBubble, TeachingBubbleCornerPlacement } from "VSSUI/TeachingBubble";
import { KeyCode, getSafeId } from "VSSUI/Util";
import { FILTER_CHANGE_EVENT, IFilter, IFilterState } from "VSSUI/Utilities/Filter";
import { Location } from "VSSUI/Utilities/Position";
import { IBacklogSettingsPanelProps } from "Wit/BacklogsHub/BacklogSettings/Components/BacklogSettingsPanel.Props";
import { MappingPane } from "Wit/BacklogsHub/MappingPane/MappingPane";
import * as Resources from "Wit/BacklogsHub/Resources";
import { BacklogLevelConfiguration } from "Wit/Clients/Work/Contracts/Work";
import { BacklogAnalyticsView } from "Wit/Common/AgileControls/BacklogAnalyticsView";
import { getBacklogLevelSelector } from "Wit/Common/AgileControls/BacklogLevelSelector";
import { IAutomationRulesService } from "Wit/Common/AutomationRulesService/AutomationRulesService";
import { IWorkItemSelection } from "Wit/Common/BacklogContent/Contracts/Contracts";
import { IEffortDataProviderResponse } from "Wit/Common/BacklogContentService/BacklogContentDataProviderTypes";
import { HubType } from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { IBacklogWorkItemService } from "Wit/Common/BacklogContentService/IBacklogWorkItemService";
import { IBacklogsHubContentService } from "Wit/Common/BacklogContentService/IBacklogsHubContentService";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { BacklogToolpaneExtension } from "Wit/Common/BacklogToolpaneExtension/BacklogToolpaneExtension";
import { WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature } from "Wit/Common/Constants/CustomerInteligence";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { ICurrentDirectoryService } from "Wit/Common/CurrentDirectoryService/ICurrentDirectoryService";
import { IHubErrorProps } from "Wit/Common/HubError/Components/HubError.props";
import { ExceptionInfo } from "Wit/Common/HubError/Models/Models";
import { PlanningPane } from "Wit/Common/PlanningPane/PlanningPane";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { ITeamSettingsPanelService } from "Wit/Common/TeamSettingsPanel/Services/TeamSettingsPanelService";
import * as TeamSettingsResources from "Wit/Common/Utils/TeamSettingsPanel/Resources";
import { getTeamSettingsCommandBarButton } from "Wit/Common/Utils/TeamSettingsPanel/TeamSettingsPanelButton";
import { FilterManager, isFilterStateEmpty } from "Wit/Filter/FilterManager";
import { mapToFilterState } from "Wit/Filter/WorkItemFilter";
import { getBoardsMessagesService } from "Wit/Messages/BoardsMessagesService";
import { BoardsMessages } from "Wit/Messages/Components/BoardsMessages";
import * as React from "react";
import { BacklogsHeader } from "../BacklogsHeader/Components/BacklogsHeader";
import { IProductBacklogTabContext } from "../Common/BacklogContracts";
import { IBacklogsHubHeaderService } from "../Common/Services/BacklogsHubHeaderService";
import { IBacklogsViewService } from "../Common/Services/BacklogsViewService";
import { renderBacklogSettingsPanel } from "../Common/Utils";
import { BacklogViewWrapper } from "./BacklogViewWrapper";

const BacklogsPath = "Backlogs";
const PanelKey = `${BacklogsPath}/Panel`;
const SettingsKey = "setting";
const TeamKey = "WebTeam";

const c_toggleSetting = "Agile/BacklogsHub";
const c_inProgressToggleSetting = "InProgressFilter";
const c_showParentsToggleSetting = "ShowParentsFilter";
const c_showCompletedToggleSetting = "CompletedChildItemsFilter";
const c_keepHierarchyToggleSetting = "KeepHierarchyWithFilters";
const c_showForecastToggleSetting = "Forecasting";

const c_agileCommon = "Agile/CommonSettings";
const c_backlogLevelBubbleSetting = "HasUserSeenBacklogLevelLearningBubble";
const c_rightPane = "Agile/RightPanel";
const c_viewOptionsBubbleSetting = "HasUserSeenViewOptionsLearningBubble";
const c_panelSplitterSize = `${PanelKey}/PanelSplitterSize`;
const c_defaultPanelWidth = 500;
const c_keepHierarchyOptionBubbleSetting = "HasUserSeenKeepHierarchyOptionBubble";

enum PanelMode {
    Planning = "planning",
    Mapping = "Mapping",
    None = "none"
}

const performanceArea = "Board";
const performanceFeature = "backlogs-hub";

export class BacklogViewComponent extends TfsComponent<{}, {}> {
    private allBacklogLevels: BacklogLevelConfiguration[];
    private visibleBacklogLevels: IObservableArray<BacklogLevelConfiguration>;
    private bugsBehaviorObservable: IObservableValue<string | undefined>;
    private commonBacklogContentService: ICommonBacklogContentService;
    private backlogContentService: IBacklogsHubContentService;
    private backlogLevelSelector: IObservableValue<IHeaderCommandBarItem>;
    private disposeBacklogLevelSelector: () => void;
    private backlogsViewService: IBacklogsViewService;
    private completedChildItemsToggle: ObservableValue<boolean> = new ObservableValue(true);
    private keepHierarchyWithFilters: ObservableValue<boolean> = new ObservableValue(false);
    private contributedPaneMenuItems: ObservableArray<IMenuItem> = new ObservableArray([]);
    private currentDirectoryService: ICurrentDirectoryService;
    private parentBacklogLevel: IObservableValue<BacklogLevelConfiguration | undefined> = new ObservableValue(undefined);
    private filter: IFilter;
    private filterManager: FilterManager;
    private filterToggled: IObservableValue<boolean>;
    private inProgressItemsToggle: ObservableValue<boolean> = new ObservableValue(true);
    private localKeyStroke: boolean = false;
    private parentToggle: ObservableValue<boolean> = new ObservableValue(false);
    private pivotContext: IProductBacklogTabContext;
    private forecastingToggle: ObservableValue<boolean> = new ObservableValue(false);
    private rightHeaderContent: ObservableArray<IHeaderCommandBarItem> = new ObservableArray<IHeaderCommandBarItem>([]);
    private selectedTabId: ObservableValue<string> = new ObservableValue(BacklogId);
    private showMapping: ObservableValue<boolean> = new ObservableValue(false);
    private showContributedPanel: Map<string, ObservableValue<boolean>> = new Map();
    private contributionIdToShow: ObservableValue<string | undefined> = new ObservableValue(undefined);
    private showPlanning: ObservableValue<boolean> = new ObservableValue(false);
    private splitterOff: ObservableValue<boolean> = new ObservableValue(false);
    private splitterSize: number | undefined;
    private automationRulesService: IAutomationRulesService;
    private _isLoadingFirstTime = true;
    private isNewSprintsViewEnabled = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.NewSprintsBacklogsView, false);


    // we need to check 2 different contribution ids for the tabs, and then add to tabs as they get populated
    private tabs = new ObservableArray<IVssContributedTab>();
    private tabsGroup1: IReadonlyObservableArray<IVssContributedTab>;
    private tabsGroup2: IReadonlyObservableArray<IVssContributedTab>;

    private TelemetryArea = "Backlogs";
    private ParentPageName = "NewBoardsHub-Backlogs";

    private mappingMenuItem: IMenuItem = {
        checked: this.showMapping,
        id: "backlog-view-options-mapping-panel",
        onActivate: () => {
            this.showMapping.value = true;
            this.showPlanning.value = false;
            this.splitterOff.value = false;
            this.showContributedPanel.forEach(check => (check.value = false));
            this.contributionIdToShow.value = undefined;
            this.setPanelSetting(PanelMode.Mapping);
        },
        text: Resources.Mapping
    };

    private onForecastToggle = async () => {
        if (!this.forecastingToggle.value) {
            this.filter.setState(this.filter.getDefaultState());
            return;
        }
        const teamSettings = this.context.pageContext.getService<ITeamSettingsPanelService>("ITeamSettingsPanelService").getTeamPanelSettings();
        const requestProperties = {
            workItemIds: Object.keys(this.commonBacklogContentService.getBacklogTree().value?.allRows ?? {}),
            teamId: teamSettings.value
        };

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const effortData = await contributionService.getDataAsync<IEffortDataProviderResponse>(
            "ms.vss-work-web.backlogs-hub-content-effort-data-data-provider",
            requestProperties
        );

        if (effortData?.data) {
            this.backlogContentService.updateForecastEffortData(effortData.data);
        }
    };

    private onHierarchyFilterToggle = () => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature.HIERARCHY_FILTER, {
            toggled: this.keepHierarchyWithFilters.value
        });
    };

    private onParentToggle = () => {
        if (this.parentToggle.value) {
            this.forecastingToggle.value = false;
            this.setToggleSetting(`${c_toggleSetting}/${c_showForecastToggleSetting}`, false);
        }
        const forecastItem = this.backlogViewSubMenuItems.value.find(item => item.id === "backlog-view-options-forecast");
        if (forecastItem) {
            forecastItem.disabled = this.parentToggle.value;
            this.backlogViewSubMenuItems.notify({ index: 4, ...forecastItem }, "change");
        }
        this.setParentRouteValue(this.parentToggle.value);
    };

    private c_parentsSubMenuItems = [
        {
            id: "backlog-view-options-parents-header",
            itemType: MenuItemType.Header,
            text: Resources.Parents
        },
        {
            renderMenuItem: (index: number, menuItem: IMenuItem, details: IMenuItemDetails) => {
                return this.renderToggleMenuItem(
                    details,
                    index,
                    menuItem,
                    Resources.ParentsToolTip,
                    Resources.Parents,
                    this.parentToggle,
                    `${c_toggleSetting}/${c_showParentsToggleSetting}`,
                    undefined,
                    this.onParentToggle
                );
            },
            id: "backlog-view-options-parents"
        }
    ];
    private parentSubMenuItems: ObservableArray<IMenuItem> = new ObservableArray<IMenuItem>([...this.c_parentsSubMenuItems]);

    private c_forecastingSubMenuItems = [
        {
            id: "backlog-view-options-forecast-header",
            itemType: MenuItemType.Header,
            text: Resources.Forecasting
        },
        {
            renderMenuItem: (index: number, menuItem: IMenuItem, details: IMenuItemDetails) => {
                return this.renderToggleMenuItem(
                    details,
                    index,
                    menuItem,
                    this.parentToggle.value ? Resources.ForecastingDisabledTooltip : Resources.ForecastingTooltip,
                    Resources.Forecasting,
                    this.forecastingToggle,
                    `${c_toggleSetting}/${c_showForecastToggleSetting}`,
                    this.parentToggle.value,
                    this.onForecastToggle
                );
            },
            id: "backlog-view-options-forecast"
        }
    ];

    private forecastSubMenuItems: ObservableArray<IMenuItem> = new ObservableArray<IMenuItem>([...this.c_forecastingSubMenuItems]);

    // Cannot use renderMenuCell() because it contains Toggle + Checkboxes which cannot be styled using a <table> because columns have to have the same width
    // Used renderMenuItem() which allows styling but requires management of Focus
    private backlogViewCommonSubMenuItems: ObservableArray<IMenuItem> = new ObservableArray<IMenuItem>([
        {
            id: "backlog-view-options-in-progress-items-header",
            itemType: MenuItemType.Header,
            text: Resources.InProgressItems
        },
        {
            renderMenuItem: (index: number, menuItem: IMenuItem, details: IMenuItemDetails) => {
                return this.renderToggleMenuItem(
                    details,
                    index,
                    menuItem,
                    Resources.InProgressItemsTooltip,
                    Resources.InProgressItems,
                    this.inProgressItemsToggle,
                    `${c_toggleSetting}/${c_inProgressToggleSetting}`
                );
            },
            id: "backlog-view-options-in-progress-items"
        },
        {
            id: "backlog-view-options-completed-child-items-header",
            itemType: MenuItemType.Header,
            text: Resources.CompletedChildItems
        },
        {
            renderMenuItem: (index: number, menuItem: IMenuItem, details: IMenuItemDetails) => {
                return this.renderToggleMenuItem(
                    details,
                    index,
                    menuItem,
                    Resources.CompletedChildItemsTooltip,
                    Resources.CompletedChildItems,
                    this.completedChildItemsToggle,
                    `${c_toggleSetting}/${c_showCompletedToggleSetting}`
                );
            },
            id: "backlog-view-options-completed-child-items"
        },
        {
            id: "backlog-view-options-keep-hierarchy-with-filters-header",
            itemType: MenuItemType.Header,
            text: Resources.KeepHierarchyWithFilters
        },
        {
            renderMenuItem: (index: number, menuItem: IMenuItem, details: IMenuItemDetails) => {
                return this.renderToggleMenuItem(
                    details,
                    index,
                    menuItem,
                    Resources.KeepHierarchyOptionBubbleText,
                    Resources.KeepHierarchyWithFilters,
                    this.keepHierarchyWithFilters,
                    `${c_toggleSetting}/${c_keepHierarchyToggleSetting}`,
                    false,
                    this.onHierarchyFilterToggle
                );
            },
            id: "backlog-view-options-keep-hierarchy-with-filters"
        },
        { id: "separator", itemType: MenuItemType.Divider },
        {
            id: "backlog-view-options-side-pane",
            itemType: MenuItemType.Header,
            text: Resources.SidePane
        },
        this.mappingMenuItem,
        {
            checked: this.showPlanning,
            id: "backlog-view-options-planning-panel",
            onActivate: () => {
                this.showMapping.value = false;
                this.showPlanning.value = true;
                this.splitterOff.value = false;
                this.showContributedPanel.forEach(check => (check.value = false));
                this.contributionIdToShow.value = undefined;
                this.setPanelSetting(PanelMode.Planning);
            },
            text: Resources.Planning
        }
    ]);

    private backlogViewOptionsPanelOffOption = {
        checked: this.splitterOff,
        id: "backlog-view-options-no-panel",
        onActivate: () => this.onActivatePanelOff(),
        text: Resources.Off
    };

    private backlogViewSubMenuItems = new ObservableCollection<IMenuItem>();

    private backlogViewOptions: IHeaderCommandBarItem = {
        ariaLabel: Resources.ViewOptions,
        className: "backlogs-view-options",
        hideDropdownIcon: true,
        iconProps: { iconName: "Equalizer" },
        id: "backlog-view-options",
        important: !this.isNewSprintsViewEnabled,
        subMenuProps: {
            id: "backlog-view-options-menu",
            items: this.backlogViewSubMenuItems
        },
        subtle: true,
        tooltipProps: { text: Resources.ViewOptions }
    };

    private fullScreenButton: IHeaderCommandBarItem = {
        ariaLabel: Resources.FullScreenEnter,
        iconProps: { iconName: "FullScreen" },
        id: "full-screen",
        important: !this.isNewSprintsViewEnabled,
        onActivate: () => {
            const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.toggleFullScreenMode();
            if (layoutManager.getFullScreenMode().value) {
                this.fullScreenButton.iconProps = { iconName: "BackToWindow" };
                this.fullScreenButton.ariaLabel = Resources.FullScreenExit;
            } else {
                this.fullScreenButton.iconProps = { iconName: "FullScreen" };
                this.fullScreenButton.ariaLabel = Resources.FullScreenEnter;
            }
            this.rightHeaderContent.change(this.rightHeaderContent.value.indexOf(this.fullScreenButton), this.fullScreenButton);
        },
        subtle: true
    };

    private toggleFilterButton: IHeaderCommandBarItem = getFilterItem(
        () => (this.filterToggled.value = !this.filterToggled.value),
        /* isFiltering */ false,
        /* important */ true,
        Resources.Filter
    );

    private lastRefresh = new ObservableValue(new Date());
    private refreshMenuItem: IHeaderCommandBarItem = {
        ariaLabel: Resources.Refresh,
        iconProps: { iconName: "Refresh" },
        id: "refresh",
        onActivate: () => {
            this.lastRefresh.value = new Date();
        },
        subtle: true
    };

    private settingsButton: IHeaderCommandBarItem;

    private showViewOptionsBubble = new ObservableValue(false);
    private showBacklogLevelBubble = new ObservableValue(false);
    private showKeepHierarchyOptionBubble = new ObservableValue(false);

    private headerExceptionMessage: ObservableValue<ExceptionInfo | undefined> = new ObservableValue(undefined);

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);
        this.backlogContentService = this.context.pageContext.getService<IBacklogsHubContentService>("IBacklogsHubContentService");
        this.backlogsViewService = this.context.pageContext.getService<IBacklogsViewService>("IBacklogsViewService");
        this.commonBacklogContentService = this.context.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        this.currentDirectoryService = this.context.pageContext.getService<ICurrentDirectoryService>("ICurrentDirectoryService");
        const backlogsHubHeaderService = this.context.pageContext.getService<IBacklogsHubHeaderService>("IBacklogsHubHeaderService");
        this.visibleBacklogLevels = backlogsHubHeaderService.getVisibleBacklogLevels();

        const teamSettingsService = this.context.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        const teamSettingsObservable: IObservableValue<ITeamSettings | undefined> = teamSettingsService.getTeamSettingsObservable();
        this.bugsBehaviorObservable = new ObservableValue<string | undefined>(teamSettingsObservable.value?.bugsBehavior?.toString());

        const isTeamAutomationRulesEnabled = isFeatureFlagEnabled(context.pageContext, FeatureFlags.TeamAutomationRulesEnabled, false);

        const currentBacklogLevel = this.backlogContentService.getBacklogLevel();

        this.getContributedPaneItems();
        this.backlogViewSubMenuItems.push(this.parentSubMenuItems);
        this.backlogViewSubMenuItems.push(this.forecastSubMenuItems);
        this.backlogViewSubMenuItems.push(this.backlogViewCommonSubMenuItems);
        this.backlogViewSubMenuItems.push(this.contributedPaneMenuItems);
        this.backlogViewSubMenuItems.push([this.backlogViewOptionsPanelOffOption]);

        this.pivotContext = {
            level: currentBacklogLevel.value,
            workItemTypes: this.visibleBacklogLevels.value.find(level => level.name === currentBacklogLevel.value)?.workItemTypes ?? [],
            team: {
                id: this.teamData?.id ?? "",
                name: this.teamData?.name ?? ""
            },
            foregroundInstance: true
        };

        if (isTeamAutomationRulesEnabled) {
            const backlogSettingsData = this.backlogContentService.getBacklogSettingsDataObservable();
            this.automationRulesService = this.context.pageContext.getService<IAutomationRulesService>("IAutomationRulesService");
            this.settingsButton = this.getBacklogSettingsCommandBarButton(this.context.pageContext, {
                backlogSettingsData: backlogSettingsData,
                teamId: this.currentDirectoryService.getCurrentDirectory().value?.id || "",
                onSave: async () => {
                    // the value has changed, but we can't query it because it won't be returned from the server for a bit
                    // we have to figure it out from what information we can gather here
                    const teamSettings = this.context.pageContext
                        .getService<ITeamSettingsPanelService>("ITeamSettingsPanelService")
                        .getTeamPanelSettings();

                    const visibleBacklogLevels: BacklogLevelConfiguration[] = [];

                    if (teamSettings.value?.backlogVisibilitySettings) {
                        this.allBacklogLevels.forEach(level => {
                            const visiblitySetting = teamSettings.value!.backlogVisibilitySettings.find(setting => setting.id === level.id);

                            if (visiblitySetting?.checked.value === true) {
                                visibleBacklogLevels.push(level);
                            }
                        });
                    }

                    this.visibleBacklogLevels.value = visibleBacklogLevels;
                    this.bugsBehaviorObservable.value = teamSettings.value?.bugsBehavior.value;

                    //we want rules to be reloaded after the settings are saved
                    this.automationRulesService.reloadRules();

                    return true;
                }
            });
        } else {
            this.settingsButton = getTeamSettingsCommandBarButton(this.context.pageContext, {
                teamId: this.currentDirectoryService.getCurrentDirectory().value?.id || "",
                onSave: async () => {
                    // the value has changed, but we can't query it because it won't be returned from the server for a bit
                    // we have to figure it out from what information we can gather here
                    const teamSettings = this.context.pageContext
                        .getService<ITeamSettingsPanelService>("ITeamSettingsPanelService")
                        .getTeamPanelSettings();

                    const visibleBacklogLevels: BacklogLevelConfiguration[] = [];

                    if (teamSettings.value?.backlogVisibilitySettings) {
                        this.allBacklogLevels.forEach(level => {
                            const visiblitySetting = teamSettings.value!.backlogVisibilitySettings.find(setting => setting.id === level.id);

                            if (visiblitySetting?.checked.value === true) {
                                visibleBacklogLevels.push(level);
                            }
                        });
                    }

                    this.visibleBacklogLevels.value = visibleBacklogLevels;
                    this.bugsBehaviorObservable.value = teamSettings.value?.bugsBehavior.value;

                    return true;
                }
            });
        }

        const { levelSelector, dispose } = getBacklogLevelSelector({
            artifactName: Resources.BacklogArtifactName,
            backlogLevels: this.visibleBacklogLevels,
            currentBacklogLevelName: currentBacklogLevel,
            iconProps: { iconName: "BacklogList" }
        });

        this.backlogLevelSelector = levelSelector;
        this.disposeBacklogLevelSelector = dispose;

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();
        if (state.pivot) {
            this.selectedTabId.value = state.pivot;
        }

        this.onSelectedTabChanged(this.selectedTabId.value);

        const backlogWorkItemService = this.context.pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
        this.filter = this.commonBacklogContentService.getFilter();
        this.filterManager = backlogWorkItemService.getFilterManager();

        this.filterToggled = new ObservableValue(false);

        this.tabsGroup1 = getTabs(context.pageContext, "ms.vss-work-web.product-backlog-tabs", { contextualData: this.pivotContext });
        this.tabsGroup2 = getTabs(context.pageContext, "ms.vss-work-web.backlogs-hub:content:pivots", { contextualData: this.pivotContext });
        this.onTabLoad();
        this.tabsGroup1.subscribe(this.onTabLoad);
        this.tabsGroup2.subscribe(this.onTabLoad);

        const getPanelSettings = async () => {
            const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
            const panelSetting: { [key: string]: string } | undefined = await settingsService.getEntriesAsync(
                PanelKey,
                SettingsUserScope.Me,
                TeamKey,
                this.currentDirectoryService.getCurrentDirectory().value?.id || ""
            );

            const setContributedPanel = (contributionId: string) => {
                this.showContributedPanel.get(contributionId)!.value = true;
                this.contributionIdToShow.value = contributionId;
            };
            if (panelSetting && panelSetting[SettingsKey]) {
                panelSetting[SettingsKey] === PanelMode.Mapping
                    ? (this.showMapping.value = true)
                    : panelSetting[SettingsKey] === PanelMode.Planning
                    ? (this.showPlanning.value = true)
                    : this.showContributedPanel.has(panelSetting[SettingsKey])
                    ? setContributedPanel(panelSetting[SettingsKey])
                    : (this.splitterOff.value = true);
            } else {
                this.showPlanning.value = true;
            }
        };

        const getToggleOptions = async () => {
            const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
            const toggleSettings: { [key: string]: string } | undefined = await settingsService.getEntriesAsync(
                c_toggleSetting,
                SettingsUserScope.Me,
                TeamKey,
                this.currentDirectoryService.getCurrentDirectory().value?.id || ""
            );

            if (!toggleSettings) {
                return;
            }
            if (toggleSettings[c_inProgressToggleSetting] !== undefined) {
                this.inProgressItemsToggle.value = Boolean(toggleSettings[c_inProgressToggleSetting]);
            }
            if (toggleSettings[c_showForecastToggleSetting] !== undefined) {
                this.forecastingToggle.value = Boolean(toggleSettings[c_showForecastToggleSetting]);
            }

            if (toggleSettings[c_showParentsToggleSetting] !== undefined) {
                if (state.showParents === undefined) {
                    const showParents = Boolean(toggleSettings[c_showParentsToggleSetting]);
                    this.parentToggle.value = showParents;
                    this.setParentRouteValue(showParents, true);
                } else {
                    this.parentToggle.value = state.showParents === "true";
                    this.commonBacklogContentService.setShouldShowOrderValues(!this.parentToggle.value);
                    if (this.parentToggle.value) {
                        this.forecastingToggle.value = false;
                    }
                }
            }
            if (toggleSettings[c_showCompletedToggleSetting] !== undefined) {
                this.completedChildItemsToggle.value = Boolean(toggleSettings[c_showCompletedToggleSetting]);
            }
            if (toggleSettings[c_keepHierarchyToggleSetting] !== undefined) {
                this.keepHierarchyWithFilters.value = Boolean(toggleSettings[c_keepHierarchyToggleSetting]);
            }
        };
        getPanelSettings();
        getToggleOptions();

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const splitterSizeSettings: { [key: string]: string } | undefined = settingsService.getEntry(c_panelSplitterSize, SettingsUserScope.Me);

        if (splitterSizeSettings && splitterSizeSettings[SettingsKey]) {
            this.splitterSize = Number(splitterSizeSettings[SettingsKey]) ?? c_defaultPanelWidth;
        }
    }

    public async componentDidMount(): Promise<void> {
        super.componentDidMount();

        const getHeaderError = async () => {
            const backlogsHubHeaderService = this.context.pageContext.getService<IBacklogsHubHeaderService>("IBacklogsHubHeaderService");
            this.headerExceptionMessage.value = await backlogsHubHeaderService.getHeaderError();
        };
        getHeaderError();

        const currentBacklogLevel: IObservableValue<string> = this.backlogContentService.getBacklogLevel();

        const resetFilterBar = async () => {
            //reset state based on new backlogs level from settings
            //TODO: Make this load from settings rather than reset if settings are available
            this.filter.reset(true);
            this.filterToggled.value = false;
            this.commonBacklogContentService.getShowFilterHeader().value = false;
        };

        const disposeCurrentBacklogLevel = currentBacklogLevel.subscribe(newLevel => {
            if (newLevel) {
                const messagesService = getBoardsMessagesService(this.context.pageContext);
                messagesService.dismissMessage();
                this.setParentBacklogLevel();
                const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

                const state = historyService.getState();
                state.backlogLevel = newLevel;
                historyService.pushState(state);
                this.setDocumentTitle();

                this.pivotContext.level = newLevel;
                this.pivotContext.workItemTypes = this.visibleBacklogLevels.value.find(level => level.name === newLevel)?.workItemTypes ?? [];

                // on new level, close filter toggle or load that filter's historical filter
                resetFilterBar();

                // Publish telemetry when user select work item from dropdown
                publishEvent(this.context.pageContext, this.TelemetryArea, "AnalyticsTab", {
                    Scenario: "DropDownClick-WorkItemType",
                    TeamId: this.teamData?.id,
                    ParentPageName: this.ParentPageName
                });
            }
        });

        const disposeBacklogLevelSelector = this.backlogLevelSelector.subscribe(newLevelSelector => {
            // backlog level selector is the first item on the right side
            this.rightHeaderContent.change(0, newLevelSelector);
        });

        const prevDispose = this.disposeBacklogLevelSelector;
        this.disposeBacklogLevelSelector = () => {
            prevDispose();
            currentBacklogLevel.unsubscribe(disposeCurrentBacklogLevel);
            this.backlogLevelSelector.unsubscribe(disposeBacklogLevelSelector);
        };

        const backlogsHubHeaderService = this.context.pageContext.getService<IBacklogsHubHeaderService>("IBacklogsHubHeaderService");
        const [allBacklogLevels] = await Promise.all([
            backlogsHubHeaderService.getAllBacklogLevels(),
            this.backlogContentService.updateBacklogLevel()
        ]);

        this.allBacklogLevels = allBacklogLevels;
        if (currentBacklogLevel.value) {
            this.setParentBacklogLevel();
        }

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const agileSettings = await settingsService.getEntriesAsync(c_agileCommon, SettingsUserScope.Me);
        this.showBacklogLevelBubble.value = !agileSettings[c_backlogLevelBubbleSetting];
        this.showKeepHierarchyOptionBubble.value = !agileSettings[c_keepHierarchyOptionBubbleSetting];

        this.filter.applyChanges();
        this.filterToggled.value = Object.keys(this.filter.getAppliedState()).length > 0;

        this.filter.subscribe(this.unsetForecastingToggle, FILTER_CHANGE_EVENT);
        this.setDocumentTitle();

        this.bugsBehaviorObservable.subscribe(() => this.refreshTree());

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature.NEW_BOARDS_PLATFORM, {
            source: "Backlogs"
        });
        this._isLoadingFirstTime = false;
    }

    public async componentWillUnmount(): Promise<void> {
        this.tabsGroup1.unsubscribe(this.onTabLoad);
        this.tabsGroup2.unsubscribe(this.onTabLoad);
        this.filter.unsubscribe(this.unsetForecastingToggle);
        this.bugsBehaviorObservable.unsubscribe(() => this.refreshTree());
        this.disposeBacklogLevelSelector();
    }

    public render(): JSX.Element {
        const resetRightHeaderSubMenuOptions = () => {
            if (!this.backlogContentService.getIsRequirementBacklog()) {
                this.forecastSubMenuItems.value = [];
            } else {
                this.forecastSubMenuItems.value = this.c_forecastingSubMenuItems;
            }

            if (this.backlogContentService.getIsRootBacklog()) {
                this.parentSubMenuItems.value = [];
            } else {
                this.parentSubMenuItems.value = this.c_parentsSubMenuItems;
            }

            return true;
        };

        const backlogViewOptionElement = document.getElementsByClassName("backlogs-view-options");

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className={`backlogs-page flex-grow ${this.isNewSprintsViewEnabled ? 'backlogs-page-sprints-ff-enabled' : ''}`} selectedTabId={this.selectedTabId} tabProviders={this.tabs}>
                    <BoardsMessages className="flex-noshrink min-icon-height" />
                    { !this.isNewSprintsViewEnabled && (<BacklogsHeader />)}
                    <Observer exceptionInfo={this.headerExceptionMessage}>
                        {(exceptionProps: { exceptionInfo: ExceptionInfo | undefined }) => {
                            if (exceptionProps.exceptionInfo) {
                                return (
                                    <WrappedComponent<IHubErrorProps>
                                        loadingComponent={renderLoading}
                                        dependencies={["ms.vss-work-web.hub-error"]}
                                        wrappedProps={{
                                            exceptionsInfo: [exceptionProps.exceptionInfo]
                                        }}
                                        wrappedType="ms.vss-work-web.hub-error-component"
                                    />
                                );
                            }
                            return (
                                <>
                                    <Observer
                                        backlogLevel={this.backlogContentService.getBacklogLevel()}
                                        backlogTree={{
                                            observableValue: this.commonBacklogContentService.getBacklogTree(),
                                            filter: resetRightHeaderSubMenuOptions
                                        }}
                                        currentDir={this.currentDirectoryService.getCurrentDirectory()}
                                        filter={{
                                            observableValue: this.filter,
                                            filter: () => {
                                                this.setFilterIcon();
                                            }
                                        }}
                                    >
                                        {(observedProps: { backlogLevel: string }) => {
                                            return (
                                                <>
                                                    { this.isNewSprintsViewEnabled ?
                                                        (
                                                            <>
                                                                <BacklogsHeader commandBarItems={this.renderLeftHeaderContent(this.selectedTabId, observedProps.backlogLevel)} />
                                                                <Header className="backlogs-tabbar-header">
                                                                    <TabBar
                                                                        className="flex-self-start full-width flex justify-space-between flex-wrap"
                                                                        tabsClassName="backlogs-tabbar-tabs"
                                                                        selectedTabId={this.selectedTabId}
                                                                        onSelectedTabChanged={(tabId: string) => this.onSelectedTabChanged(tabId)}
                                                                        renderAdditionalContent={() =>
                                                                            <HeaderCommandBar useAriaLabelForOverflow={true} items={this.rightHeaderContent.value} />
                                                                        }
                                                                        tabSize={TabSize.Compact}
                                                                    >
                                                                        <Tab id={BacklogId} name={Resources.Backlog} />
                                                                        <Tab id={AnalyticsId} name={Resources.Analytics} />
                                                                    </TabBar>
                                                                </Header>
                                                            </>
                                                        ) :
                                                        (
                                                            <Header className="backlogs-tabbar-header" commandBarItems={this.rightHeaderContent}>
                                                                <TabBar
                                                                    className="flex-self-start full-width flex"
                                                                    tabsClassName="backlogs-tabbar-tabs"
                                                                    selectedTabId={this.selectedTabId}
                                                                    onSelectedTabChanged={(tabId: string) => this.onSelectedTabChanged(tabId)}
                                                                    renderAdditionalContent={() =>
                                                                        <HeaderCommandBar useAriaLabelForOverflow={true} className="flex-grow backlogs-header-flex-start" items={this.renderLeftHeaderContent(this.selectedTabId, observedProps.backlogLevel)} />  
                                                                    }
                                                                    tabSize={TabSize.Compact}
                                                                >
                                                                    <Tab id={BacklogId} name={Resources.Backlog} />
                                                                    <Tab id={AnalyticsId} name={Resources.Analytics} />
                                                                </TabBar>
                                                            </Header>
                                                        )
                                                    }
                                                    <Observer
                                                        backlogLevelSelector={this.backlogLevelSelector}
                                                        showBacklogLevelBubble={this.showBacklogLevelBubble}
                                                    >
                                                        {(props: {
                                                            backlogLevelSelector: IHeaderCommandBarItem;
                                                            showBacklogLevelBubble: boolean;
                                                        }) => {
                                                            return props.showBacklogLevelBubble &&
                                                                props.backlogLevelSelector &&
                                                                document.getElementsByClassName("backlog-level-selector").length > 0 ? (
                                                                <TeachingBubble
                                                                    anchorElement={
                                                                        document.getElementsByClassName("backlog-level-selector")[0] as HTMLElement
                                                                    }
                                                                    anchorOrigin={{
                                                                        horizontal: Location.end,
                                                                        vertical: Location.end
                                                                    }}
                                                                    cornerPlacement={TeachingBubbleCornerPlacement.vertical}
                                                                    onDismiss={() => {
                                                                        this.showBacklogLevelBubble.value = false;
                                                                        this.setTeachingBubbleSetting(
                                                                            `${c_agileCommon}/${c_backlogLevelBubbleSetting}`
                                                                        );
                                                                    }}
                                                                    primaryButtonProps={{
                                                                        text: Resources.GotIt,
                                                                        primary: true,
                                                                        onClick: () => {
                                                                            this.showBacklogLevelBubble.value = false;
                                                                            this.setTeachingBubbleSetting(
                                                                                `${c_agileCommon}/${c_backlogLevelBubbleSetting}`
                                                                            );
                                                                        }
                                                                    }}
                                                                    text={Resources.BacklogLevelTeachingBubbleText}
                                                                    title={Resources.BacklogPicker}
                                                                />
                                                            ) : null;
                                                        }}
                                                    </Observer>
                                                    <Observer showViewOptionsBubble={this.showViewOptionsBubble}>
                                                        {(props: { showViewOptionsBubble: boolean }) => {
                                                            return props.showViewOptionsBubble && backlogViewOptionElement.length > 0 ? (
                                                                <TeachingBubble
                                                                    anchorElement={backlogViewOptionElement[0] as HTMLElement}
                                                                    anchorOrigin={{
                                                                        horizontal: Location.end,
                                                                        vertical: Location.end
                                                                    }}
                                                                    cornerPlacement={TeachingBubbleCornerPlacement.vertical}
                                                                    onDismiss={() => {
                                                                        this.showViewOptionsBubble.value = false;
                                                                        this.setTeachingBubbleSetting(`${c_rightPane}/${c_viewOptionsBubbleSetting}`);
                                                                    }}
                                                                    primaryButtonProps={{
                                                                        text: Resources.GotIt,
                                                                        primary: true,
                                                                        onClick: () => {
                                                                            this.showViewOptionsBubble.value = false;
                                                                            this.setTeachingBubbleSetting(
                                                                                `${c_rightPane}/${c_viewOptionsBubbleSetting}`
                                                                            );
                                                                        }
                                                                    }}
                                                                    text={Resources.PaneClosedTeachingBubbleText}
                                                                    title={Resources.PanelClosed}
                                                                />
                                                            ) : null;
                                                        }}
                                                    </Observer>
                                                    <Observer showKeepHierarchyOptionBubble={this.showKeepHierarchyOptionBubble}>
                                                        {(props: { showKeepHierarchyOptionBubble: boolean }) => {
                                                            return props.showKeepHierarchyOptionBubble &&
                                                                this.isKeepHierarchyOptionBubbleEnabled() &&
                                                                backlogViewOptionElement.length > 0 ? (
                                                                <TeachingBubble
                                                                    anchorElement={backlogViewOptionElement[0] as HTMLElement}
                                                                    anchorOrigin={{
                                                                        horizontal: Location.end,
                                                                        vertical: Location.end
                                                                    }}
                                                                    cornerPlacement={TeachingBubbleCornerPlacement.vertical}
                                                                    onDismiss={() => {
                                                                        this.showKeepHierarchyOptionBubble.value = false;
                                                                        this.setTeachingBubbleSetting(
                                                                            `${c_agileCommon}/${c_keepHierarchyOptionBubbleSetting}`
                                                                        );
                                                                    }}
                                                                    primaryButtonProps={{
                                                                        text: Resources.TryIt,
                                                                        primary: true,
                                                                        onClick: () => {
                                                                            this.showKeepHierarchyOptionBubble.value = false;
                                                                            this.keepHierarchyWithFilters.value = true;
                                                                            this.setToggleSetting(
                                                                                `${c_toggleSetting}/${c_keepHierarchyToggleSetting}`,
                                                                                true
                                                                            );
                                                                            this.setTeachingBubbleSetting(
                                                                                `${c_agileCommon}/${c_keepHierarchyOptionBubbleSetting}`
                                                                            );
                                                                            this.refreshTree();
                                                                        }
                                                                    }}
                                                                    text={
                                                                        <>
                                                                            <div>{Resources.KeepHierarchyOptionBubbleText}</div>
                                                                            <Link href={Resources.KeepHierarchyOptionBubbleLink}>
                                                                                {Resources.LearnMore}.
                                                                            </Link>
                                                                        </>
                                                                    }
                                                                    title={Resources.KeepHierarchyWithFilters}
                                                                />
                                                            ) : null;
                                                        }}
                                                    </Observer>
                                                </>
                                            );
                                        }}
                                    </Observer>
                                    <Observer selectedTabId={this.selectedTabId}>
                                        {(observedProps: { selectedTabId: string }) => {
                                            if (observedProps.selectedTabId === BacklogId) {
                                                const pageContent = (
                                                    <BacklogViewWrapper
                                                        filter={this.filter}
                                                        filterToggled={this.filterToggled}
                                                        filterManager={this.filterManager}
                                                        keepHierarchyWithFilters={this.keepHierarchyWithFilters}
                                                        toggleParents={this.onParentToggle}
                                                        onItemSelected={this.notifyOnItemSelected}
                                                        onMarkTTI={this.completeBacklogTTIScenario}
                                                    />
                                                );

                                                return (
                                                    <Observer
                                                        contributionId={this.contributionIdToShow}
                                                        showMapping={this.showMapping}
                                                        showPlanning={this.showPlanning}
                                                    >
                                                        {(observedProps: {
                                                            contributionId: string | undefined;
                                                            showMapping: boolean;
                                                            showPlanning: boolean;
                                                        }) => {
                                                            const showSidePanel =
                                                                observedProps.showMapping ||
                                                                observedProps.showPlanning ||
                                                                observedProps.contributionId;
                                                            return (
                                                                <Splitter
                                                                    maxFixedSize={1000}
                                                                    minFixedSize={300}
                                                                    className={`scroll-hidden ${!this.isNewSprintsViewEnabled ? 'full-height' : ''}`}
                                                                    farElementClassName="flex-column"
                                                                    nearElementClassName="flex-column"
                                                                    initialFixedSize={this.splitterSize}
                                                                    onFixedSizeChanged={this.onSplitterSizeChange}
                                                                    onRenderNearElement={() => pageContent}
                                                                    onRenderFarElement={
                                                                        showSidePanel
                                                                            ? () => {
                                                                                  if (observedProps.showMapping) {
                                                                                      return (
                                                                                          <MappingPane
                                                                                              targetBacklogLevel={this.parentBacklogLevel}
                                                                                              visibleBacklogLevels={this.visibleBacklogLevels.value}
                                                                                              onDismiss={() => this.onActivatePanelOff()}
                                                                                          />
                                                                                      );
                                                                                  } else if (observedProps.showPlanning) {
                                                                                      return (
                                                                                          <PlanningPane
                                                                                              hub={HubType.Backlogs}
                                                                                              onDismiss={() => this.onActivatePanelOff()}
                                                                                          />
                                                                                      );
                                                                                  } else {
                                                                                      // show extension
                                                                                      return (
                                                                                          <BacklogToolpaneExtension
                                                                                              title={
                                                                                                  this.contributedPaneMenuItems.value.find(
                                                                                                      item => item.id === observedProps.contributionId
                                                                                                  )!.data!.title
                                                                                              }
                                                                                              team={this.commonBacklogContentService.getCurrentTeam()}
                                                                                              contributionId={observedProps.contributionId}
                                                                                              onDismiss={() => this.onActivatePanelOff()}
                                                                                          />
                                                                                      );
                                                                                  }
                                                                              }
                                                                            : undefined
                                                                    }
                                                                />
                                                            );
                                                        }}
                                                    </Observer>
                                                );
                                            } else if (observedProps.selectedTabId === AnalyticsId) {
                                                return (
                                                    <Observer
                                                        backlogLevel={this.backlogContentService.getBacklogLevel()}
                                                        visibleBacklogLevels={this.visibleBacklogLevels}
                                                    >
                                                        {(observerProps: {
                                                            backlogLevel: string;
                                                            visibleBacklogLevels: BacklogLevelConfiguration[];
                                                        }) => {
                                                            return (
                                                                <div className="page-content-top page-content flex-grow flex-row h-scroll-hidden">
                                                                    <BacklogAnalyticsView
                                                                        teamId={this.teamData?.id ?? ""}
                                                                        teamName={this.teamData?.name ?? ""}
                                                                        projectId={this.projectData?.id ?? ""}
                                                                        categoryReferenceName={
                                                                            observerProps.visibleBacklogLevels.find(
                                                                                level => level.name === observerProps.backlogLevel
                                                                            )?.id ?? ""
                                                                        }
                                                                        categoryName={observerProps.backlogLevel}
                                                                        lastRefresh={this.lastRefresh}
                                                                        onLoadCompleted={() => {}}
                                                                    />
                                                                </div>
                                                            );
                                                        }}
                                                    </Observer>
                                                );
                                            }
                                            return (
                                                <Observer
                                                    backlogLevel={this.backlogContentService.getBacklogLevel()}
                                                    visibleBacklogLevels={this.visibleBacklogLevels}
                                                >
                                                    {(observerProps: { backlogLevel: string }) => {
                                                        // key used to force full rerender of tab when backlog level changes
                                                        return (
                                                            <div className="page-content-top page-content flex-grow flex-row h-scroll-hidden">
                                                                <TabContent key={"tab:" + observerProps.backlogLevel} />
                                                            </div>
                                                        );
                                                    }}
                                                </Observer>
                                            );
                                        }}
                                    </Observer>
                                </>
                            );
                        }}
                    </Observer>
                </Page>
            </Surface>
        );
    }

    private startBacklogTTIScenario(properties?: { [key: string]: any }) {
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const scenario = performanceService.startScenario(performanceFeature, true);
        if (properties) {
            scenario.addProperties(properties);
        }
    }

    private completeBacklogTTIScenario = () => {
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(performanceArea, performanceFeature, undefined, {
            tab: this.selectedTabId.value
        });
    };

    private refreshTree(): void {
        this.backlogContentService.refreshTree({
            filterManager: this.filterManager
        });
    }

    private renderToggleMenuItem(
        details: IMenuItemDetails,
        index: number,
        menuItem: IMenuItem,
        tooltipText: string,
        ariaLabel: string,
        toggleButtonState: ObservableValue<boolean>,
        settingsKey: string,
        disabled?: boolean,
        onToggle?: () => void
    ): JSX.Element {
        return (
            // This must handle its own focus to allow menu to collapse and be interactable with keyboard shortcuts when used with renderMenuItem()
            <FocusZoneContext.Consumer>
                {rowContext => (
                    <FocusWithin onFocus={event => details.onFocusItem(index, event)}>
                        {(focusStatus: IFocusWithinStatus) => (
                            <FocusZone direction={FocusZoneDirection.Horizontal}>
                                <tr
                                    className="bolt-menuitem-row bolt-list-row bolt-menuitem-row-normal cursor-pointer"
                                    role="menuitem"
                                    tabIndex={-1}
                                    id={getSafeId(menuItem.id)}
                                    onBlur={focusStatus.onBlur}
                                    onFocus={focusStatus.onFocus}
                                    onKeyDown={this.onKeyDown}
                                    onKeyUp={event => this.onKeyUp(event, () => (toggleButtonState.value = !toggleButtonState.value))}
                                    data-focuszone={rowContext.focuszoneId}
                                >
                                    <td className="bolt-menuitem-cell bolt-list-cell" colSpan={7}>
                                        <Toggle
                                            ariaLabel={ariaLabel}
                                            className="margin-vertical-8 padding-left-16"
                                            disabled={disabled}
                                            excludeFocusZone={true}
                                            offText={Resources.Off}
                                            onText={Resources.On}
                                            checked={toggleButtonState}
                                            onChange={(event, value) => {
                                                toggleButtonState.value = value;
                                                event.preventDefault();
                                                this.setToggleSetting(settingsKey, value);
                                                onToggle && onToggle();
                                                this.refreshTree();
                                                this.commonBacklogContentService.resetOrderColumn();
                                            }}
                                            tooltipProps={{
                                                text: tooltipText
                                            }}
                                        />
                                    </td>
                                </tr>
                            </FocusZone>
                        )}
                    </FocusWithin>
                )}
            </FocusZoneContext.Consumer>
        );
    }

    private getContributedPaneItems = async () => {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const menuId = this.backlogContentService.getIsRequirementBacklog()
            ? "ms.vss-work-web.requirement-backlog-toolpane"
            : "ms.vss-work-web.portfolio-backlog-toolpane";

        const contributionIds = contributionService.getContributionChildren(menuId);
        if (!contributionIds) {
            return;
        }

        const contributions = (await contributionService.getContributionsAsync(contributionIds)).filter(contribution => contribution);
        if (!contributions || !contributions.length) {
            return;
        }

        contributions.forEach((contribution, index) => this.showContributedPanel.set(contributionIds[index], new ObservableValue(false)));
        this.contributedPaneMenuItems.value = contributions.map((item: any, index): IMenuItem => {
            return {
                id: contributionIds[index],
                checked: this.showContributedPanel.get(contributionIds[index]),
                text: item.name,
                data: { ...item },
                onActivate: menuItem => {
                    this.showMapping.value = false;
                    this.showPlanning.value = false;
                    this.splitterOff.value = false;
                    this.showContributedPanel.forEach((value, key) => {
                        value.value = key === menuItem.id;
                    });
                    this.contributionIdToShow.value = menuItem.id;
                    this.setPanelSetting(menuItem.id);
                }
            };
        });
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.which === KeyCode.enter || event.which === KeyCode.space) {
            this.localKeyStroke = true;
        }
    };

    private onKeyUp = (event: React.KeyboardEvent<HTMLElement>, toggleButtonState: () => void) => {
        // If we get focus while a key is down we will get the keyup. We don't want
        // to process this key, unless it needs to originates from us inside the menu.
        if (!this.localKeyStroke) {
            return;
        }

        this.localKeyStroke = false;

        if (!event.defaultPrevented) {
            if (event.which === KeyCode.enter || event.which === KeyCode.space) {
                toggleButtonState();
            }
        }
    };

    private onSplitterSizeChange = (value: number) => {
        this.splitterSize = value;
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const entries: { [key: string]: any } = {
            [`${c_panelSplitterSize}/${SettingsKey}`]: value
        };
        settingsService.setEntries(entries, SettingsUserScope.Me);
    };

    private async setParentBacklogLevel(): Promise<void> {
        const backlogLevel = this.backlogContentService.getBacklogLevel();
        if (this.allBacklogLevels && this.allBacklogLevels.length > 0 && backlogLevel.value) {
            const currentIndex: number = this.allBacklogLevels.findIndex(bl => bl.name === backlogLevel.value);
            if (currentIndex > 0) {
                this.parentBacklogLevel.value = this.allBacklogLevels[currentIndex - 1];
                // If the mapping view menu item was removed due to the parent backlog level being undefined, add it back
                const mappingViewIndex: number = this.backlogViewCommonSubMenuItems.value.findIndex(item => item.id === this.mappingMenuItem.id);
                if (mappingViewIndex === -1) {
                    this.backlogViewCommonSubMenuItems.splice(mappingViewIndex, 0, this.mappingMenuItem);
                }
            } else {
                this.parentBacklogLevel.value = undefined;
                // Check if the mapping pane is open, and if so close it because there is no parent backlog level
                if (this.showMapping.value) {
                    this.onActivatePanelOff();
                }
                // Since there is no parent backlog level, remove the mapping view menu item
                const mappingViewIndex: number = this.backlogViewCommonSubMenuItems.value.findIndex(item => item.id === this.mappingMenuItem.id);
                if (mappingViewIndex !== -1) {
                    this.backlogViewCommonSubMenuItems.splice(mappingViewIndex, 1);
                }
            }
        }
    }

    private setTeachingBubbleSetting(settingsKey: string) {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        settingsService.setEntries(
            {
                [settingsKey]: true
            },
            SettingsUserScope.Me
        );
    }

    private onActivatePanelOff = async () => {
        this.showContributedPanel.forEach(check => (check.value = false));
        this.showMapping.value = false;
        this.showPlanning.value = false;
        this.splitterOff.value = true;
        this.contributionIdToShow.value = undefined;
        this.setPanelSetting(PanelMode.None);

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const rightPanel = await settingsService.getEntriesAsync(c_rightPane, SettingsUserScope.Me);
        this.showViewOptionsBubble.value = !rightPanel[c_viewOptionsBubbleSetting];
    };

    private setPanelSetting = (mode: PanelMode | string) => {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const panelEntries: { [key: string]: any } = {
            [`${PanelKey}/${SettingsKey}`]: mode
        };
        settingsService.setEntries(panelEntries, SettingsUserScope.Me, TeamKey, this.currentDirectoryService.getCurrentDirectory().value?.id || "");
    };

    private setParentRouteValue = (showParents: boolean, shouldHistoryBeReplaced?: boolean) => {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();
        state.showParents = String(showParents);
        this.commonBacklogContentService.setShouldShowOrderValues(!showParents);
        shouldHistoryBeReplaced ? historyService.replaceState(state) : historyService.pushState(state);
    };

    private setToggleSetting = (key: string, value: boolean) => {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const toggleEntry: { [key: string]: any } = {
            [`${key}`]: value
        };
        settingsService.setEntries(toggleEntry, SettingsUserScope.Me, TeamKey, this.currentDirectoryService.getCurrentDirectory().value?.id || "");
    };

    private onSelectedTabChanged(tabId: string): void {
        if (!this._isLoadingFirstTime){
            this.startBacklogTTIScenario({
                inPageNavigation: true,
                switchTab: true,
                tab: tabId
            });
        }

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();
        if (state.pivot !== tabId) {
            state.pivot = tabId;
            historyService.pushState(state);
            this.selectedTabId.value = tabId;

            // publish when user clicks on Analytics Tab
            if(tabId === AnalyticsId){
                publishEvent(this.context.pageContext, this.TelemetryArea, "AnalyticsTab", {
                    ParentPageName: this.ParentPageName,
                    Scenario: "AnalyticsTabAccess"
                })
            }  
        }

        if (tabId === BacklogId) {
            this.rightHeaderContent.value = [
                this.backlogLevelSelector.value,
                this.backlogViewOptions,
                this.toggleFilterButton,
                this.settingsButton,
                this.fullScreenButton
            ];
        } else if (tabId === AnalyticsId) {
            this.rightHeaderContent.value = [this.backlogLevelSelector.value, this.refreshMenuItem, this.fullScreenButton];
        } else {
            this.rightHeaderContent.value = [this.backlogLevelSelector.value, this.fullScreenButton];
        }
    }

    private onTabLoad = () => {
        this.tabs.value = [...this.tabsGroup1.value, ...this.tabsGroup2.value];
    };
    private renderLeftHeaderContent(selectedTabId: ObservableValue<string>, backlogLevel: string): IHeaderCommandBarItem[] {
        if (selectedTabId.value === BacklogId && this.commonBacklogContentService.getBacklogTree().value) {
            const headerItems = this.commonBacklogContentService.getBacklogCommandBarItems(this.backlogContentService);
            headerItems.splice(1, 0, {
                iconProps: { iconName: "NavigateForward" },
                id: "view-as-board",
                itemType: MenuItemType.Normal,
                href: this.backlogsViewService.getBoardsUrl(backlogLevel, this.currentDirectoryService.getCurrentDirectory().value?.name ?? ""),
                rank: 2,
                subtle: !this.isNewSprintsViewEnabled,
                text: Resources.ViewAsBoard
            });
            return headerItems;
        }

        return [];
    }

    private unsetForecastingToggle = (value: IFilterState) => {
        if (this.forecastingToggle.value && !isFilterStateEmpty(mapToFilterState(value))) {
            this.backlogContentService.resetForecastSettings();
            this.forecastingToggle.value = false;
            this.setToggleSetting(`${c_toggleSetting}/${c_showForecastToggleSetting}`, false);
        }
    };

    private setDocumentTitle = (): void => {
        const currentTeam = this.currentDirectoryService.getCurrentDirectory().value?.name;
        const currentBacklogLevel = this.backlogContentService.getBacklogLevel().value;
        if (currentTeam) {
            document.title = format(Resources.BacklogsDocumentTitle, currentTeam, currentBacklogLevel);
        }
    };

    private setFilterIcon = (): void => {
        const filterButtonIndex = this.rightHeaderContent.value.indexOf(this.toggleFilterButton);
        if (filterButtonIndex === -1) {
            return;
        }

        const isFiltering = this.filterManager.isFiltering();
        this.toggleFilterButton.iconProps = getFilterItemIconProps(isFiltering);
        this.rightHeaderContent.change(filterButtonIndex, this.toggleFilterButton);
    };

    private notifyOnItemSelected = async (selectedWorkItems: IWorkItemSelection[]): Promise<void> => {
        if (this.contributionIdToShow.value) {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            const contribution: any = await contributionService.getContributionAsync(this.contributionIdToShow.value);
            const registeredObjectId = contribution?.registeredObjectId;

            if (registeredObjectId) {
                const legacyService = this.context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
                const [contributionControls] = await legacyService.requireModules(["VSS/Contributions/Controls"]);

                const contributionHost = await contributionControls.getBackgroundHost(this.contributionIdToShow.value);
                const registeredObject = await contributionHost.getRegisteredInstance(registeredObjectId);

                registeredObject.workItemSelectionChanged?.(selectedWorkItems);
            }
        }
    };

    private isKeepHierarchyOptionBubbleEnabled = (): boolean => {
        return !isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.KeepHierarchyOptionBubbleDisabled, false);
    };

    private getBacklogSettingsCommandBarButton = (
        pageContext: IVssPageContext,
        panelProps: Omit<IBacklogSettingsPanelProps, "onDismiss">
    ): IHeaderCommandBarItem => {
        const settingsButton: IHeaderCommandBarItem = {
            ariaLabel: TeamSettingsResources.ConfigureTeamSettings,
            iconProps: { iconName: "Settings" },
            id: "team-settings",
            important: true,
            onActivate: () => renderBacklogSettingsPanel(pageContext, panelProps),
            subtle: true,
            tooltipProps: { text: TeamSettingsResources.ConfigureTeamSettings }
        };

        return settingsButton;
    };
}

function renderLoading() {
    return <Spinner size={SpinnerSize.large} />;
}

const AnalyticsId = "analytics";
const BacklogId = "backlog";

Components.add("backlog-view-component", BacklogViewComponent);
